/**
 * ABRECHNUNG: DIE EINE SEITE (.340).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (10.10.2026)
 *   „Du musst diese Seite überarbeiten ... bringe das alles auf eine Seite
 *    mit Rechnungsprüfung, Rechnung bezahlen ... Ich schicke das Aufmaß weg,
 *    dann kriegen wir die Genehmigung. In dem Fall erhalten wir eine
 *    Gutschrift. Die Firma schreibt eine Rechnung, wenn die ja die Info von
 *    mir bekommen, hey, Gutschrift ist da, schreib bitte eine Rechnung ...
 *    Die wird dann zugeordnet, wird geprüft, ob sie dann so mit der
 *    Gutschrift übereinstimmt und kommt dann ganz normal mit
 *    Fälligkeitsdatum in den Zahlungslauf."
 *
 * DREI SCHRITTE JE AUFTRAG, als Kacheln oben; darunter die Abschnitte
 * untereinander (seit .365 eine Maske, siehe unten). Die bisherigen Seiten
 * „Rechnungen prüfen", „Rechnungen bezahlen", „Gebühren bezahlen" und
 * „LEB-Abrechnung" sind die ANSICHTEN dieser Seite: ihre Bausteine werden
 * hier eingesetzt, nicht nachgebaut.
 *
 * KEINE E-MAIL AN EINEN PARTNER. Was hinausgeht, ist die kurze Push-Nachricht
 * in die Telegram-Gruppe der Firma: Freigabe wie eingereicht oder mit den
 * Positionen, die auf der Rechnung anders stehen als im Aufmaß (Position, Menge), nie ein Preis.
 */

import { useState } from 'react';
import { merke, rollbereich, useMerkDaten, useMerkState } from '../teile/merkplatz.ts';
import { api, darf, datum, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Suchfeld, passtSuche } from '../teile/Schluesselsuche.tsx';
import { Rechnungsfinder } from '../teile/Rechnungsfinder.tsx';
import { Abrechnungsfreigabe } from '../teile/Abrechnungsfreigabe.tsx';
import { Rechnungsfreigabe } from '../teile/Rechnungsfreigabe.tsx';
import { Rechnungspruefung, Prueffenster } from '../teile/Rechnungspruefung.tsx';
import { Partnerzahlung } from '../teile/Partnerzahlung.tsx';
import Gebuehren from './Gebuehren.tsx';
import LebAbrechnung from './LebAbrechnung.tsx';
import { Zahlungsavise } from '../teile/Zahlungsavise.tsx';

type Ansicht = 'start' | 'gebuehren' | 'gutschriften' | 'avise';
type Schritt = 'schritt1' | 'schritt2' | 'schritt3';

/* EINE MASKE, SAUBER ZUM SCROLLEN (.365).
 * ============================================================================
 * ORIGINALTEXT MARVIN (14.10.2026)
 *   „baue das entsprechend um und ein, damit wir da endlich und final Ruhe
 *    haben und nicht zwölf verschiedene Ansichten. Da kann man klicken, da
 *    fehlt was, da wurde was noch nicht angefordert oder oder. Das will ich
 *    in einer Maske haben. Also sauber zum Scrollen."
 *
 * Die drei Schritte sind keine Unterseiten mehr, sondern Abschnitte
 * untereinander: die Kacheln oben springen hin. Die Tabelle „Was heute
 * ansteht" ist weg, ihr Inhalt steht an den Zeilen der Abschnitte selbst.
 * Gebühren und Gutschriften bleiben eigene Seiten, sie sind ein anderes
 * Thema. */

const ANSICHT_TITEL: Record<Ansicht, string> = {
  start: 'Abrechnung',
  gebuehren: 'Gebühren der Behörden',
  gutschriften: 'Gutschriften der Telekom',
  avise: 'Zahlungsavise der Telekom',
};

export default function Rechnungswesen({
  ich, oeffne, gehZu, startSchritt,
}: { ich: Anmelder; oeffne: (id: string, reiter?: string) => void; gehZu?: (wo: string) => void; startSchritt?: Schritt }) {
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* MIT GEDÄCHTNIS (.398), siehe `merkplatz.ts`: Kacheln und Listen stehen
     beim Zurückkommen aus der Akte sofort, frisch geladen wird dahinter. */
  const [d, , nachladen] = useMerkDaten<any>('abrechnung.stand', () => api.abrechnungStand(), (f) => setFehler(ausFehler(f)));
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [ansicht, setAnsicht] = useState<Ansicht>('start');
  const [suche, setSuche] = useMerkState('abrechnung.suche', '');
  const [laeuft, setLaeuft] = useState<string | null>(null);
  /* DAS PRÜFFENSTER AUS DER SUCHE (.408). Marvin: „muss ich irgendwie dahin
     kommen, dass ich auch dahin springen kann zum Prüfen." */
  const [pruefe, setPruefe] = useMerkState<string | null>('abrechnung.pruefe', null);

  async function laden() {
    setFehler(null);
    await nachladen();
  }
  useFrisch(laden);

  const zeilen: any[] = d?.zeilen ?? [];

  async function tu(k: string, f: () => Promise<string | null>) {
    setLaeuft(k); setFehler(null); setHinweis(null);
    try { const t = await f(); if (t) setHinweis(t); await laden(); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(null); }
  }

  const s = d?.schritte;
  const darfRechte = d?.darf ?? {};
  /* EIN SCHRITT ZUR ZEIT (.389). ORIGINALTEXT MARVIN (17.10.2026): „Warum
     ist das eine Riesenseite, wo dann alles zu scrollen ist? Dafür habe ich
     doch oben die verschiedenen Punkte. Das macht sonst so zum Scrollen,
     denke ich, einfach nur Verwirrung." Am 14.10. hiess es noch „in einer
     Maske ... sauber zum Scrollen"; die Kacheln bleiben die eine Maske, aber
     sie schalten um, statt zu springen. Der gewählte Schritt wird gemerkt. */
  const [schritt, setSchritt] = useState<Schritt | 'alle'>(() => {
    /* DIE ALTE ADRESSE „RECHNUNGEN PRÜFEN" FÜHRT HIERHER, SCHRITT 2 (.433).
       Marvin: „Thema Abrechnung und Rechnung prüfen, das sind doch bei uns
       die Schritte 1, 2, 3." */
    if (startSchritt) return startSchritt;
    try { const g = localStorage.getItem('abrechnung.schritt'); return g === 'schritt2' || g === 'schritt3' || g === 'alle' ? g : 'schritt1'; } catch { return 'schritt1'; }
  });
  const springe = (wo: Schritt | 'alle') => {
    setSchritt(wo);
    try { localStorage.setItem('abrechnung.schritt', wo); } catch { /* egal */ }
    /* Ein Wechsel der Kachel fängt oben an; nur der Weg zurück aus der Akte stellt die Stelle wieder her (.479). */
    for (const k of ['schritt1', 'schritt2', 'schritt3']) merke(`roll:abrechnung.${k}`, 0);
    try { rollbereich().setze(0); } catch { /* egal */ }
  };

  /* ---------------------------------------------------- die Nebenseiten */
  if (ansicht !== 'start') {
    return (
      <>
        <div className="inhalt" style={{ paddingBottom: 0 }}>
          <div className="reihe" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="knopf klein" onClick={() => { setAnsicht('start'); laden(); }}>← Abrechnung</button>
            <b>{ANSICHT_TITEL[ansicht]}</b>
          </div>
        </div>
        {ansicht === 'gebuehren' && (darfRechte.gebuehren
          ? <Gebuehren ich={ich} gehZu={gehZu} oeffne={oeffne} />
          : <div className="inhalt"><div className="hinweis">Zu den Gebühren fehlt dir das Recht.</div></div>)}
        {ansicht === 'gutschriften' && <LebAbrechnung ich={ich} oeffne={oeffne} gehZu={gehZu} />}
        {ansicht === 'avise' && <Zahlungsavise />}
      </>
    );
  }

  /* ------------------------------------------------------ die eine Maske */
  return (
    <>
      <Kopf titel="Abrechnung"
            weg="Gutschrift, Rechnung, Zahlung: drei Schritte, die Kacheln schalten um"
            rechts={
              <div className="reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Suchfeld wert={suche} setze={setSuche} mehr="Rechnungsnr., Firma" breit={260} />
                {darfRechte.gebuehren && (
                  <button className="knopf klein" onClick={() => setAnsicht('gebuehren')}>
                    Gebühren{d ? ` (${d.gebuehren.erwartet} erwartet, ${d.gebuehren.offen} offen)` : ''}
                  </button>
                )}
                {darf(ich, 'abrechnung.geld') && <button className="knopf klein" onClick={() => setAnsicht('gutschriften')}>Gutschriften</button>}
                {darf(ich, 'abrechnung.geld') && <button className="knopf klein" onClick={() => setAnsicht('avise')}>Zahlungsavise</button>}
                {gehZu && <button className="knopf klein" onClick={() => gehZu('bankverbindungen')}>Bankverbindungen</button>}
              </div>
            } />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}

        {s && (
          <div className="schrittkacheln">
            <button type="button" className={`schrittkachel ${schritt === 'schritt1' ? 'an' : ''}`} aria-pressed={schritt === 'schritt1'} onClick={() => springe('schritt1')}>
              <span className="schritt">Schritt 1</span>
              <span className="titel">Gutschrift da, Rechnung anfordern</span>
              {/* DIE SUMME IST NACHRECHENBAR (.387). Marvin, 17.10.2026: „Du
                  schreibst oben 84 Gutschriften sind da. Aber unten bei der
                  Auflistung zu den Firmen komme ich nicht auf die Anzahl 84."
                  Die grosse Zahl ist die Liste „Firma informieren"; die
                  Angeforderten stehen als zweite Zahl daneben und in der
                  zweiten Liste darunter. */}
              <span className="zahl">{s.anfordern}{s.angefordert ? <small className="dazu"> + {s.angefordert}</small> : null}</span>
              <span className="klein leise">
                {s.anfordern} {s.anfordern === 1 ? 'Gutschrift da' : 'Gutschriften da'}, Firma informieren (Liste „Firma informieren")
                {s.angefordert ? `, ${s.angefordert} angefordert, Rechnung noch nicht da (Liste „Angefordert")` : ''}
                {s.ueberfaellig ? `, davon ${s.ueberfaellig} überfällig` : ''}
              </span>
            </button>
            <button type="button" className={`schrittkachel ${schritt === 'schritt2' ? 'an' : ''}`} aria-pressed={schritt === 'schritt2'} onClick={() => springe('schritt2')}>
              <span className="schritt">Schritt 2</span>
              <span className="titel">Rechnung gegen Gutschrift prüfen</span>
              <span className="zahl">{s.pruefen}</span>
              <span className="klein leise">
                {s.pruefen ? `${s.pruefen} ${s.pruefen === 1 ? 'braucht' : 'brauchen'} einen Menschen` : 'nichts braucht einen Menschen'}
                {s.unerwartet ? `, ${s.unerwartet} unerwartet` : ''}{s.zurueckgestellt ? `, ${s.zurueckgestellt} zurückgestellt` : ''}.
                {s.von_selbst ? ` ${s.von_selbst} gingen in 30 Tagen von selbst durch.` : ''}
              </span>
            </button>
            <button type="button" className={`schrittkachel ${schritt === 'schritt3' ? 'an' : ''}`} aria-pressed={schritt === 'schritt3'} onClick={() => springe('schritt3')}>
              <span className="schritt">Schritt 3</span>
              <span className="titel">Zahlung</span>
              <span className="zahl">{s.faellig + s.in_revolut}</span>
              <span className="klein leise">{s.faellig} fällig, {s.in_revolut} in Revolut zur Freigabe, {s.bezahlt_woche} diese Woche bezahlt</span>
            </button>
          </div>
        )}

        {/* DIE SUCHE SAGT, WO DIE RECHNUNG IST (.406), egal welcher Schritt
            offen ist. Marvin (19.10.2026, RE1307/RE1308): „eine Suchfunktion,
            dass ich auch nach Rechnungsnummern suchen kann, wenn irgendwas
            nicht stimmt". Die Rechnungen lagen im Belege-Ordner (Schritt 3),
            gesucht hat er in Schritt 2. */}
        {schritt !== 'alle' && suche.trim() && <Rechnungsfinder suche={suche} springe={springe} pruefe={darf(ich, 'abrechnung.rechnung') ? setPruefe : undefined} />}
        {pruefe && <Prueffenster ich={ich} id={pruefe} zu={() => setPruefe(null)} fertig={() => { laden(); }} />}

        {/* ---------------------------------------------------- Schritt 1 */}
        {schritt === 'schritt1' && (<>
        <h2 id="schritt1" className="abschnitt">Schritt 1, Gutschrift da, Rechnung anfordern</h2>
        {darf(ich, 'abrechnung.freigeben')
          ? <Abrechnungsfreigabe ich={ich} suche={suche} oeffne={oeffne} />
          : <div className="hinweis">Zum Freigeben fehlt dir das Recht „zur Abrechnung freigeben".</div>}
        <Angefordert zeilen={zeilen.filter((z) => z.schritt === 1 && z.freigabe_id && passtSuche(suche, z.kls_id, z.sm_nummer, z.anschrift, z.firma))}
                     laeuft={laeuft} tu={tu} oeffne={oeffne} darf={!!darfRechte.freigeben} />
        </>)}

        {/* ---------------------------------------------------- Schritt 2 */}
        {schritt === 'schritt2' && (<>
        <h2 id="schritt2" className="abschnitt">Schritt 2, Rechnung gegen Gutschrift prüfen</h2>
        <p className="klein leise" style={{ marginTop: 0 }}>
          Jede Rechnung aus dem Postfach wird dem Auftrag zugeordnet und gegen Anforderung, Gutschrift und Aufmaß
          gehalten. Rot ist, was fehlt oder abweicht; orange ist ein Hinweis (andere Positionsnummern, keine Positionen
          gelesen, Montage mit eigenen Positionen) und hält die Freigabe nicht auf. Jeder rote oder orange Punkt ist
          anklickbar und öffnet das Prüffenster. Freigegeben wird von dir; von selbst erst mit RECHNUNG_VON_SELBST auf ja.
          {darfRechte.rechnung_freigeben && (
            <> <button className="alslink" disabled={!!laeuft}
                       onClick={() => tu('lauf', async () => (await api.abrechnungLauf()).klartext)}>Lauf jetzt</button></>
          )}
        </p>
        <Rechnungsfreigabe ich={ich} suche={suche} oeffne={oeffne} />
        </>)}

        {/* ---------------------------------------------------- Schritt 3 */}
        {schritt === 'schritt3' && (<>
        <h2 id="schritt3" className="abschnitt">Schritt 3, Zahlung</h2>
        <p className="klein leise" style={{ marginTop: 0 }}>
          Was in Schritt 2 durch ist, steht hier mit seiner Fälligkeit, je Partner gebündelt. „Zahlungslauf" macht
          daraus je Partner Überweisungen als Entwurf in Revolut, jede Rechnungsnummer im Verwendungszweck. Du gibst
          in der App frei; der Abgleich mit dem Konto setzt die Rechnungen auf bezahlt. Nichts wird von hier aus
          gesendet, nur vorbereitet.
        </p>
        {darfRechte.bezahlen ? <Partnerzahlung ich={ich} />
          : <div className="hinweis">Zum Bezahlen fehlt dir das Recht „Rechnungen der Partner bezahlen".</div>}
        </>)}

        {/* --------------------------------------------- alle Rechnungen */}
        {schritt === 'alle' && (<>
          <h2 id="alle" className="abschnitt">Alle Rechnungen der Subunternehmer</h2>
          <p className="klein leise" style={{ marginTop: 0 }}>Auch freigegebene und bezahlte, mit Erfassen von Hand.</p>
          <Rechnungspruefung ich={ich} oeffne={oeffne} vorgabe={null} suche={suche} />
        </>)}
        {schritt !== 'alle' && (
          <p style={{ marginTop: 16 }}>
            <button type="button" className="knopf klein" onClick={() => springe('alle')}>Alle Rechnungen der Subunternehmer</button>
          </p>
        )}

        <div className="dreispaltig klein leise">
          <div><b>Was von selbst läuft:</b> Rechnung zuordnen, gegen Anforderung und Gutschrift halten, abhaken, Fälligkeit setzen, Abgleich mit dem Konto. Freigeben und Bezahlen tust du; bezahlt wird nur, was geprüft ist.</div>
          <div><b>Was hinausgeht:</b> nur die kurze Push-Nachricht in die Telegram-Gruppe der Firma, Freigabe wie eingereicht oder mit den Positionen, die auf der Rechnung anders stehen müssen als im Aufmaß der Firma (Position, Menge). Das Aufmaß selbst bleibt, es ist eingereicht und beschieden. Nie ein Preis, keine E-Mail.</div>
          <div><b>Was liegen bleiben darf:</b> eine Rechnung, die vor ihrer Gutschrift kommt, kann zurückgestellt werden, bis der Auftrag freigegeben ist, oder gelöscht werden, ohne Sperre: kommt sie später noch einmal, wird sie wieder angelegt.</div>
        </div>
      </div>
    </>
  );
}

/* ANGEFORDERT, RECHNUNG NOCH NICHT DA: mit dem Nachweis, dass der Push
   rausging (.365), Erinnerung per Push, und seit .393 je Firma gesammelt.
   ORIGINALTEXT MARVIN (17.10.2026)
     „Sammelpush für jede Firma nochmal machen ... dann kann ich die alle
      markieren, selektieren und kann sagen, hier nochmal Erinnerungspush
      senden." „der Erinnerungspush muss natürlich dann auch wieder als Chip
      eingetragen sein, wann wir die Erinnerung geschickt haben. Wichtig ist,
      dass du den Rechnungseingang wirklich gegenprüfst." */
function Angefordert({ zeilen, laeuft, tu, oeffne, darf: darfErinnern }: {
  zeilen: any[]; laeuft: string | null;
  tu: (k: string, f: () => Promise<string | null>) => Promise<void>;
  oeffne: (id: string, reiter?: string) => void;
  darf: boolean;
}) {
  const [firma, setFirma] = useMerkState<string | null>('abrechnung.angefordert.firma', null);
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const firmen = [...new Set(zeilen.map((z) => String(z.firma ?? '')))].filter(Boolean).sort((a, b) => a.localeCompare(b, 'de'));
  const sicht = zeilen.filter((z) => !firma || z.firma === firma);
  const gewaehlt = sicht.filter((z) => wahl.has(z.freigabe_id));
  const alle = sicht.length > 0 && sicht.every((z) => wahl.has(z.freigabe_id));
  const ohneAuftrag = new Map<string, number>();
  for (const z of zeilen) if (z.rechnungen_ohne_auftrag) ohneAuftrag.set(z.firma, Math.max(ohneAuftrag.get(z.firma) ?? 0, z.rechnungen_ohne_auftrag));
  return (
    <Karte titel={`Angefordert, Rechnung noch nicht da (${zeilen.length})`}
           rechts={darfErinnern && zeilen.length > 0 ? (
             <button className="knopf haupt klein" disabled={!gewaehlt.length || !!laeuft}
                     onClick={() => tu('erinnern', async () => (await api.freigabenErinnern(gewaehlt.map((z) => z.freigabe_id))).klartext)}>
               {firma ? `Erinnerung an ${firma} senden (${gewaehlt.length})` : `Erinnerung senden, je Firma eine (${gewaehlt.length})`}
             </button>
           ) : undefined}>
      {!zeilen.length ? <Leer text="Jede angeforderte Rechnung ist eingegangen." /> : (
        <>
          <div className="ga-filter" style={{ marginBottom: 8 }}>
            <button type="button" className={`ga-chip ${!firma ? 'an' : ''}`} aria-pressed={!firma} onClick={() => setFirma(null)}>alle {zeilen.length}</button>
            {firmen.map((f) => (
              <button key={f} type="button" className={`ga-chip ${firma === f ? 'an' : ''}`} aria-pressed={firma === f} onClick={() => { setFirma(f); setWahl(new Set()); }}>
                {f} {zeilen.filter((z) => z.firma === f).length}
                {ohneAuftrag.get(f) ? <span className="fr-rot" title="Rechnungen dieser Firma seit dem Push, die keinem Auftrag zugeordnet sind: erst zuordnen, dann erinnern">{ohneAuftrag.get(f)}</span> : null}
              </button>
            ))}
          </div>
          {[...ohneAuftrag.entries()].filter(([f]) => !firma || f === firma).map(([f, n]) => (
            <div key={f} className="hinweis fehler" style={{ marginBottom: 8 }}>
              <b>{f}:</b> {n} {n === 1 ? 'Rechnung' : 'Rechnungen'} seit dem Push im Postfach ohne zugeordneten Auftrag. Erst zuordnen (Alle Rechnungen der Subunternehmer), sonst erinnerst du an etwas, das schon da ist.
            </div>
          ))}
          <label className="klein leise" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <input type="checkbox" checked={alle} onChange={() => { const n = new Set(wahl); for (const z of sicht) { if (alle) n.delete(z.freigabe_id); else n.add(z.freigabe_id); } setWahl(n); }} />
            alle {sicht.length} wählen
          </label>
          <div className="freigabeliste">
            {sicht.map((z) => (
              <div key={z.freigabe_id} className="freigabezeile">
                <input type="checkbox" aria-label="wählen" checked={wahl.has(z.freigabe_id)}
                       onChange={() => { const n = new Set(wahl); if (n.has(z.freigabe_id)) n.delete(z.freigabe_id); else n.add(z.freigabe_id); setWahl(n); }} />
                <span className="freigabezeile-text">
                  <button className="alslink" onClick={() => oeffne(z.auftrag_id, 'leb')}><b>{z.anschrift}</b></button>
                  <span className="klein leise">
                    {' '}{z.firma}{z.kls_id ? `, KLS ${z.kls_id}` : ''}{z.gutschrift_nummer ? `, GS ${z.gutschrift_nummer}` : ''}, freigegeben {datum(z.seit)}
                    {z.tage != null ? `, ${z.tage} ${z.tage === 1 ? 'Tag' : 'Tage'} her` : ''}
                    {z.abweichungen ? `, ${z.abweichungen} ${z.abweichungen === 1 ? 'Änderung' : 'Änderungen'} genannt` : ''}
                  </span>
                </span>
                {/* DER PUSH, SCHWARZ AUF WEISS. Marvin, 14.10.2026: „dass sie
                    überhaupt angefordert wurden. Das kann ich mich nicht dran
                    erinnern." Das Nachrichtenprotokoll sagt es. */}
                {z.push === null || z.push === undefined
                  ? <span className="marke-zustand z-unterbrochen" title="Im Nachrichtenprotokoll steht kein Telegram-Versand zu dieser Freigabe">Push: kein Eintrag</span>
                  : z.push.zugestellt
                    ? <span className="marke-zustand z-erledigt" title={`Telegram an die Gruppe der Firma, ${z.push.wann ? datum(z.push.wann) : ''}`}>Push zugestellt {z.push.wann ? datum(z.push.wann) : ''}</span>
                    : <span className="marke-zustand z-ueberfaellig" title={z.push.grund ?? ''}>Push nicht zugestellt{z.push.grund ? `: ${z.push.grund}` : ''}</span>}
                {z.erinnert_am && <span className="marke-zustand z-neu" title="Erinnerung per Push">erinnert {datum(z.erinnert_am)}</span>}
                {z.lage === 'ueberfaellig' && <span className="marke-zustand z-ueberfaellig">überfällig</span>}
                {darfErinnern && (
                  <button className="knopf klein" disabled={!!laeuft}
                          onClick={() => tu(z.freigabe_id, async () => (await api.freigabeErinnern(z.freigabe_id)).klartext)}>
                    Erinnern
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Karte>
  );
}
